import * as React from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Button, Chip, IconButton, Switch, TextField, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import clsx from 'clsx';
import { MatterTerminology } from '../MatterTerminology';
import { SummaryViewConfigObj } from './SummaryViewConfigurations';
import _ from 'lodash';
import { SaveSummaryViewConfiguration } from './SummaryViewConfigRepository';
import { showNotification } from '../App';
import DialogBox from '../Dashboard/Component/DialogBox';
import { AnimatedItem } from '../components/animations/AnimatedItem';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      textAlign: 'left',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(2, 2, 1.5),
    },
    headerIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      backgroundColor: 'var(--rv-accent-blue-bg)',
      color: 'var(--rv-accent-blue)',
      '& svg': { fontSize: 22 },
    },
    headerText: {
      flex: 1,
      minWidth: 0,
    },
    headerTitle: {
      fontWeight: 600,
      lineHeight: 1.3,
    },
    sectionLabel: {
      padding: theme.spacing(0, 2, 0.5),
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
    },
    list: {
      maxHeight: 220,
      overflowY: 'auto',
      padding: theme.spacing(0, 1, 1),
    },
    emptyList: {
      padding: theme.spacing(1, 2),
      color: theme.palette.text.secondary,
      fontSize: 13,
    },
    viewRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      width: '100%',
      padding: theme.spacing(1, 1.25),
      border: '1px solid transparent',
      borderRadius: 8,
      background: 'none',
      color: theme.palette.text.primary,
      font: 'inherit',
      textAlign: 'left',
      cursor: 'pointer',
      '&:hover': { backgroundColor: 'var(--rv-accent-blue-bg)' },
    },
    viewRowSelected: {
      backgroundColor: 'var(--rv-accent-blue-bg)',
      borderColor: 'var(--rv-accent-blue)',
    },
    viewRadio: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      border: '2px solid #aab4c3',
      flexShrink: 0,
    },
    viewRadioSelected: {
      borderColor: 'var(--rv-accent-blue)',
      backgroundColor: 'var(--rv-accent-blue)',
      boxShadow: 'inset 0 0 0 3px #fff',
    },
    viewName: {
      flex: 1,
      minWidth: 0,
      fontWeight: 500,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    tag: {
      height: 20,
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: 'uppercase',
      '& .MuiChip-icon': { fontSize: 12, marginLeft: 6 },
    },
    tagFirmDefault: { backgroundColor: 'var(--rv-accent-green-bg)', color: 'var(--rv-accent-green)' },
    tagMyDefault: { backgroundColor: 'var(--rv-accent-orange-bg)', color: 'var(--rv-accent-orange)' },
    tagOnlyMe: { backgroundColor: 'var(--rv-accent-grey-bg)', color: 'var(--rv-accent-grey)' },
    editor: {
      margin: theme.spacing(0, 1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      backgroundColor: theme.palette.action.hover,
    },
    editorTop: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 1.5, 0),
    },
    editorTitle: {
      flex: 1,
      minWidth: 0,
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    editorFields: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      padding: theme.spacing(1.5),
    },
    fieldLabel: {
      display: 'block',
      marginBottom: theme.spacing(0.5),
      fontSize: 13,
      fontWeight: 600,
    },
    helper: {
      marginTop: theme.spacing(0.5),
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    visibilityGroup: {
      display: 'flex',
      width: '100%',
      backgroundColor: theme.palette.background.paper,
      '& .MuiToggleButton-root': {
        flex: 1,
        gap: theme.spacing(0.75),
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(0.75, 1),
      },
      '& .MuiToggleButton-root.Mui-selected': {
        backgroundColor: 'var(--rv-accent-blue-bg)',
        color: 'var(--rv-accent-blue)',
      },
      '& svg': { fontSize: 16 },
    },
    switchRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
    switchText: {
      flex: 1,
      minWidth: 0,
      paddingTop: theme.spacing(0.75),
    },
    lockedNote: {
      padding: theme.spacing(1, 1.25),
      borderRadius: 6,
      fontSize: 12.5,
      backgroundColor: 'var(--rv-accent-grey-bg)',
      color: 'var(--rv-accent-grey)',
    },
    columnsNote: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    footer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2, 2),
    },
  }),
);

interface SummaryViewConfigPanelProps {
  views: Array<SummaryViewConfigObj>;
  selectedConfigId: number;
  isAdministrator: boolean;
  isNewView: boolean;
  name: string;
  isPrivate: boolean;
  isDefault: boolean;
  isTenantDefault: boolean;
  isHideIsPrivate: boolean;
  isHideIsDefault: boolean;
  isHideIsTenantDefault: boolean;
  isDiableSave: boolean;
  onClose: () => void;
  onSelectView: (configId: number) => void;
  // tslint:disable-next-line: no-any
  onNameChange: (event: any) => void;
  onFlagChange: (name: string, checked: boolean) => void;
  onUpdate: () => void;
  onDelete: () => void;
  onStartNewView: () => void;
  onCreateView: () => void;
  onCancelNewView: () => void;
}

// Presentational only: every rule (who may change what, what clears what, what saves) lives in SummaryViewConfig below.
const SummaryViewConfigPanel: React.FC<SummaryViewConfigPanelProps> = (props) => {
  const classes = useStyles();
  const selectedView = _.find(props.views, (item: SummaryViewConfigObj) => item.configId === props.selectedConfigId);
  // a non-administrator cannot change a firm-wide view, but can still keep their own copy
  const isFirmViewLocked = !props.isNewView && props.isDiableSave && !!selectedView && !selectedView.isPrivate;

  const renderDefaultSwitch = (flagName: string, label: string, helper: string, checked: boolean) => (
    <div className={classes.switchRow}>
      <Switch
        size="small"
        color="primary"
        checked={checked}
        onChange={(event) => props.onFlagChange(flagName, event.target.checked)}
        inputProps={{ 'aria-label': label }}
      />
      <div className={classes.switchText}>
        <span className={classes.fieldLabel} style={{ marginBottom: 0 }}>{label}</span>
        <div className={classes.helper}>{helper}</div>
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerIcon}><ViewColumnIcon /></div>
        <div className={classes.headerText}>
          <Typography className={classes.headerTitle} variant="h6">Views</Typography>
          <Typography variant="caption" color="textSecondary">Saved column layouts for this list.</Typography>
        </div>
        <IconButton size="small" aria-label="Close views" onClick={props.onClose}><CloseIcon /></IconButton>
      </div>

      <div className={classes.sectionLabel}>Your views</div>
      <div className={classes.list} role="listbox" aria-label="Views">
        {(!props.views || props.views.length === 0) && <div className={classes.emptyList}>No saved views yet.</div>}
        {(props.views || []).map((view, index) => {
          const isSelected = !props.isNewView && view.configId === props.selectedConfigId;
          return (
            <AnimatedItem key={view.configId} noFill={true} delay={index * 20}>
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                className={clsx(classes.viewRow, isSelected && classes.viewRowSelected)}
                onClick={() => props.onSelectView(view.configId)}
              >
                <span className={clsx(classes.viewRadio, isSelected && classes.viewRadioSelected)} />
                <span className={classes.viewName} title={view.name}>{view.name}</span>
                {props.isAdministrator && view.isTenantDefault && (
                  <Chip size="small" label={MatterTerminology.FirmDefault} className={clsx(classes.tag, classes.tagFirmDefault)} />
                )}
                {view.isDefault && <Chip size="small" label="My default" className={clsx(classes.tag, classes.tagMyDefault)} />}
                {view.isPrivate && <Chip size="small" label="Only me" className={clsx(classes.tag, classes.tagOnlyMe)} />}
              </button>
            </AnimatedItem>
          );
        })}
      </div>

      <AnimatedItem key={`${props.isNewView ? 'new' : props.selectedConfigId}`} noFill={true}>
        <div className={classes.editor}>
          <div className={classes.editorTop}>
            <span className={classes.editorTitle}>
              {props.isNewView ? 'New view' : selectedView ? `${isFirmViewLocked ? 'View' : 'Edit'} “${selectedView.name}”` : 'No view selected'}
            </span>
            {!props.isNewView && (
              <IconButton size="small" aria-label="Delete view" title="Delete view" onClick={props.onDelete} disabled={props.isDiableSave}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </div>
          <div className={classes.editorFields}>
            <div>
              <label className={classes.fieldLabel} htmlFor="summary-view-name">View name</label>
              <TextField
                id="summary-view-name"
                fullWidth={true}
                variant="outlined"
                size="small"
                value={props.name}
                onChange={props.onNameChange}
                disabled={!props.isNewView && props.isDiableSave}
                placeholder={props.isNewView ? 'Name the new view' : ''}
                autoFocus={props.isNewView}
              />
            </div>

            {!props.isHideIsPrivate && (
              <div>
                <span className={classes.fieldLabel}>Who can use it</span>
                <ToggleButtonGroup
                  exclusive={true}
                  size="small"
                  className={classes.visibilityGroup}
                  value={props.isPrivate ? 'private' : 'firm'}
                  onChange={(_event, value: string | null) => {
                    if (value) {
                      props.onFlagChange('isPrivate', value === 'private');
                    }
                  }}
                  aria-label="Who can use this view"
                >
                  <ToggleButton value="private"><LockOutlinedIcon />{MatterTerminology.MyViewOnly}</ToggleButton>
                  <ToggleButton value="firm"><PeopleOutlineIcon />Whole firm</ToggleButton>
                </ToggleButtonGroup>
                <div className={classes.helper}>
                  {props.isPrivate ? 'Only you can see and use this view.' : 'Everyone in the firm can pick this view.'}
                </div>
              </div>
            )}

            {!props.isHideIsDefault && renderDefaultSwitch(
              'isDefault',
              'My default view',
              'Opens automatically for you when you come to this list.',
              props.isDefault,
            )}
            {!props.isHideIsTenantDefault && renderDefaultSwitch(
              'isTenantDefault',
              MatterTerminology.FirmDefault,
              'Opens for everyone who has not set a default of their own.',
              props.isTenantDefault,
            )}

            {isFirmViewLocked && (
              <div className={classes.lockedNote}>
                This is a firm-wide view, so only administrators can change it. You can save a copy as your own view.
              </div>
            )}
            <div className={classes.columnsNote}>Saving also stores the columns currently on screen.</div>
          </div>
        </div>
      </AnimatedItem>

      <div className={classes.footer}>
        {props.isNewView ? (
          <>
            <Button variant="contained" color="primary" disableElevation={true} onClick={props.onCreateView}>Create view</Button>
            <Button variant="outlined" color="primary" onClick={props.onCancelNewView}>Cancel</Button>
          </>
        ) : (
          <>
            <Button variant="contained" color="primary" disableElevation={true} onClick={props.onUpdate} disabled={props.isDiableSave}>
              Save changes
            </Button>
            <Button variant="outlined" color="primary" onClick={props.onStartNewView}>Save as new view</Button>
          </>
        )}
      </div>
    </div>
  );
};

export interface SummaryViewConfigState {
  show: boolean;
  isAdministrator: boolean;
  updateShow: boolean;
  deleteShow: boolean;
  saveAsShow: boolean;
  renameShow: boolean;

  configNameSaveAs: string;
  configName: string;
  configId: number;

  isPrivate: boolean;
  isTenantDefault: boolean;
  isDefault: boolean;
  isHideIsPrivate: boolean;
  isDiableSave: boolean;
  isHideIsDefault: boolean;
  isHideIsTenantDefault: boolean;
}

export interface SummaryViewConfigProps {
  views?: Array<SummaryViewConfigObj>;
  configType: number; // 1 = MatterSummary, 2 = LeadSummary
  isAdministrator: boolean;
  onClose?: (show: boolean) => void;
  onRowSelected?: (configId: number) => void;
  onViewUpdated?: () => void;
  viewConfig?: string; // Current config to be saved
  viewConfigId: number;
  isNewMatterSummary: boolean;
}

// tslint:disable-next-line:max-line-length
export class SummaryViewConfig extends React.Component<SummaryViewConfigProps, SummaryViewConfigState> {

  constructor(props: SummaryViewConfigProps, state: SummaryViewConfigState) {
    super(props, state);

    this.onClose = this.onClose.bind(this);
    this.handleControlLogic = this.handleControlLogic.bind(this);

    this.onRowSelected = this.onRowSelected.bind(this);

    this.handleTextChange = this.handleTextChange.bind(this);
    this.onCheckboxChange = this.onCheckboxChange.bind(this);
    this.handleUpdateAgreed = this.handleUpdateAgreed.bind(this);
    this.handleDeleteAgreed = this.handleDeleteAgreed.bind(this);

    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleSaveAs = this.handleSaveAs.bind(this);
    this.handleDelete = this.handleDelete.bind(this);

    this.onClicCloseDialog = this.onClicCloseDialog.bind(this);
    this.onClickSaveAs = this.onClickSaveAs.bind(this);

    this.state = {
      show: false,
      isAdministrator: this.props.isAdministrator,
      updateShow: false,
      deleteShow: false,
      saveAsShow: false,
      renameShow: false,
      configNameSaveAs: '',
      configName: '',
      isPrivate: true,
      isTenantDefault: false,
      isDefault: false,
      configId: this.props.viewConfigId,

      isHideIsPrivate: this.props.isAdministrator ? false : true,
      isDiableSave: true,
      isHideIsDefault: false,
      isHideIsTenantDefault: false
    };
  }

  public componentDidMount() {
    this.handleControlLogic('isPrivate', true, this.state);

    if (this.props.viewConfigId) {
      // tslint:disable-next-line: no-console
      // console.log('componentDidMount', this.props.viewConfigId);

      this.populateData();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: SummaryViewConfigProps, nextState: SummaryViewConfigState): void {
    if (this.props.viewConfigId !== nextProps.viewConfigId
      || this.props.isAdministrator !== nextProps.isAdministrator) {
      this.setState({
        configId: nextProps.viewConfigId,
        isAdministrator: nextProps.isAdministrator,
        isHideIsPrivate: nextProps.isAdministrator ? false : true,
      });
    }
  }

  public componentDidUpdate(prevProps: SummaryViewConfigProps, prevState: SummaryViewConfigState) {
    if (this.state.configId !== prevState.configId) {
      this.populateData();
    }
  }

  public render() {
    return (
      <>
        <DialogBox
          title="Confirmation Message"
          content="Are you sure you want to update?"
          show={this.state.updateShow}
          isAgree={this.handleUpdateAgreed}
          disAgreeLabel={'No'}
          agreeLabel={'Yes'}
        />
        <DialogBox
          title="Confirmation Message"
          content="Are you sure you want to delete?"
          show={this.state.deleteShow}
          isAgree={this.handleDeleteAgreed}
          disAgreeLabel={'No'}
          agreeLabel={'Yes'}
        />
        <SummaryViewConfigPanel
          views={this.props.views || []}
          selectedConfigId={this.state.configId}
          isAdministrator={this.state.isAdministrator}
          isNewView={this.state.saveAsShow}
          name={this.state.saveAsShow ? this.state.configNameSaveAs : this.state.configName}
          isPrivate={this.state.isPrivate}
          isDefault={this.state.isDefault}
          isTenantDefault={this.state.isTenantDefault}
          isHideIsPrivate={this.state.isHideIsPrivate}
          isHideIsDefault={this.state.isHideIsDefault}
          isHideIsTenantDefault={this.state.isHideIsTenantDefault}
          isDiableSave={this.state.isDiableSave}
          onClose={this.onClose}
          onSelectView={this.onRowSelected}
          onNameChange={this.handleTextChange}
          onFlagChange={this.onCheckboxChange}
          onUpdate={this.handleUpdate}
          onDelete={this.handleDelete}
          onStartNewView={this.handleSaveAs}
          onCreateView={this.onClickSaveAs}
          onCancelNewView={this.onClicCloseDialog}
        />
      </>
    );
  }

  // Picking a view applies it to the list (the host answers with the new viewConfigId, which repopulates this panel).
  private onRowSelected(configId: number) {
    if (this.state.saveAsShow) {
      // leaving "new view" - bring the selected view's own details back even if it is the one already showing
      this.setState({ saveAsShow: false, configNameSaveAs: '' }, () => this.populateData());
    }
    if (this.props.onRowSelected) {
      // This will send back the configId and layout.
      this.props.onRowSelected(configId);
    }
  }

  private populateData() {
    let summaryViewConfig = _.find(this.props.views, (item: SummaryViewConfigObj) => {
      return item.configId === this.state.configId;
    });

    // tslint:disable-next-line: no-console
    // console.log('summaryViewConfig', summaryViewConfig);

    let configName = '';
    let isPrivate = false;
    let isTenantDefault = false;
    let isDefault = false;
    let isDiableSave = false;

    if (summaryViewConfig !== undefined) {
      // If the template is not my view only/ personal templates, then a non-administrator user cannot update it
      if (!summaryViewConfig.isPrivate) {
        if (this.state.isAdministrator) {
          isDiableSave = false;
        } else {
          isDiableSave = true;
        }
      }

      configName = summaryViewConfig.name;
      isPrivate = summaryViewConfig.isPrivate;
      isDefault = summaryViewConfig.isDefault;
      isTenantDefault = summaryViewConfig.isTenantDefault;
    }

    // Save Tenant Default
    this.setState({
      configNameSaveAs: '',
      configName: configName,
      isPrivate: isPrivate,
      isDefault: isDefault,
      isTenantDefault: isTenantDefault,
      isDiableSave: isDiableSave
    });

    if (this.state.isAdministrator) {
      this.handleControlLogic('isPrivate', isPrivate, this.state);
    }
  }

  private onClose() {
    if (this.props.onClose !== undefined) {
      this.props.onClose(false);
    }
  }

  // tslint:disable-next-line:no-any
  private handleTextChange(event: any) {
    this.setState({
      configNameSaveAs: event.currentTarget.value,
      configName: event.currentTarget.value,
    });
  }

  private onCheckboxChange(name: string, checked: boolean) {
    let configState = this.state;
    // tslint:disable-next-line: no-any
    (configState as any)[name] = checked;

    configState = this.handleControlLogic(name, checked, configState);

    this.setState({
      ...configState
    });
  }

  private handleControlLogic(name: string | undefined, checked: boolean | undefined, configState: SummaryViewConfigState) {

    let isChecked = checked === undefined ? false : checked;

    if (name === 'isPrivate') {
      if (isChecked) {
        configState.isPrivate = true;
        configState.isTenantDefault = false;
        configState.isDefault = false;
        configState.isHideIsTenantDefault = true;
        configState.isHideIsDefault = false;
      } else {
        configState.isPrivate = false;
        configState.isTenantDefault = false;
        configState.isDefault = false;
        configState.isHideIsTenantDefault = false;
        configState.isHideIsDefault = true;
      }
    }

    if (!this.state.isAdministrator) {
      configState.isTenantDefault = false;
      configState.isHideIsTenantDefault = true;

      // if (name === 'isDefault') {

      // }

    }

    return configState;
  }

  // Update existing Record
  private handleUpdate() {
    this.setState({
      updateShow: true
    });
  }

  // Always create a new record - opens the "new view" form (name starts empty; the default/visibility switches keep
  // whatever they currently show, exactly as the old "Create New View" dialog did)
  private handleSaveAs() {
    this.setState({
      saveAsShow: true,
      configNameSaveAs: '',
      configName: ''
    });
  }

  // Delete the selected config
  private handleDelete() {
    this.setState({
      deleteShow: true
    });
  }

  // Validate the data before saving
  private validateInput(isUpdate: boolean): Boolean {
    const templateNames = this.props.views;

    let isValid: Boolean = true;

    if (isUpdate && this.state.configId === 0) {
      isValid = false;
      showNotification('Incorrect input', 'Please select a template to Rename.', 'error');
    }

    if (this.state.configName.length === 0) {
      isValid = false;
      showNotification('Incorrect input', 'Please specify the template name to continue.', 'error');
    }

    // Check for already existing Template Names.
    if (!isUpdate) {
      var templateName = _.find(templateNames, (item: SummaryViewConfigObj) => {
        return item.name === this.state.configName;
      });

      if (templateName !== undefined) {
        isValid = false;
        showNotification('Incorrect input', 'Please specify a unique template name to continue.', 'error');
      }
    }

    return isValid;
  }

  private onClickSaveAs() {
    const isValid = this.validateInput(false);

    if (isValid) {

      // Saving a new Template
      this.SaveColumnConfig(0, false);
      this.setState({
        saveAsShow: false,
        configNameSaveAs: ''
      });
    }
  }

  // Cancel "new view": go back to the selected view's own details
  private onClicCloseDialog() {
    this.setState({
      saveAsShow: false,
      renameShow: false,
      configNameSaveAs: ''
    }, () => this.populateData());
  }

  private SaveColumnConfig(configId: number, isDelete: boolean) {
    // If the user is not an administrator, then it is always a private template
    let isPrivate = !this.state.isAdministrator ? true : this.state.isPrivate;
    let isTenantDefault = !this.state.isAdministrator ? false : this.state.isTenantDefault;

    if (this.props.viewConfig !== undefined) {
      SaveSummaryViewConfiguration(
        configId,
        this.state.configName,
        false,
        isPrivate,
        this.state.isDefault,
        isTenantDefault,
        this.props.configType,
        this.props.viewConfig,
        isDelete,
        this.props.isNewMatterSummary,
        () => {
          // Success
          showNotification(null, 'Successfully submitted', 'info');
        },
        (reason) => {
          showNotification('Failed to Save Template', reason, 'error');
        }
      );
    }
  }

  private handleUpdateAgreed(isUpdate: boolean) {
    const vm = this;

    if (isUpdate) {
      const isValid = vm.validateInput(true);

      if (isValid) {
        vm.SaveColumnConfig(this.state.configId, false);
      }
    }

    this.setState({
      updateShow: false
    });
  }

  private handleDeleteAgreed(isDelete: boolean) {
    if (isDelete) {
      const isValid = this.validateInput(true);

      if (isValid) {
        this.SaveColumnConfig(this.state.configId, true);
      }
    }

    this.setState({
      deleteShow: false
    });
  }
}
